import "core-js/modules/es.array.concat";
import "core-js/modules/es.array.map";
import "core-js/modules/es.number.constructor";
import "core-js/modules/es.number.is-nan";
import "core-js/modules/es.number.parse-float";
import "core-js/modules/es.regexp.exec";
import "core-js/modules/es.string.replace";
import "core-js/modules/es.string.split";
import "core-js/modules/es.string.starts-with";
import "core-js/modules/es.string.trim";
import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import invariant from 'invariant';
import numberFormatter from '@sprinx/formatting/numberFormatter';
import numberParser from '@sprinx/formatting/numberParser';
import Globalize from '@sprinx/globalize';
import TextField from '../TextField';
import useMuiFieldsContext from '../useMuiFieldsContext';

var isEmpty = function isEmpty(s) {
  return s === undefined || s === null;
};

var formatValue = function formatValue(numberValue, decimalPrecision) {
  if (numberValue === undefined || numberValue === null) {
    return '';
  }

  invariant(typeof numberValue === 'number', 'Input value is not a number.');
  var formattedValue = numberFormatter(numberValue, decimalPrecision);
  return formattedValue;
};

var stateValue = function stateValue(dp, v) {
  return !isEmpty(v) ? formatValue(v, dp) : v;
};
/**
 */


function NumberField(_ref) {
  var allowNegative = _ref.allowNegative,
      pdp = _ref.decimalPrecision,
      outherDefaultValue = _ref.defaultValue,
      onChange = _ref.onChange,
      outherValue = _ref.value,
      rest = _objectWithoutProperties(_ref, ["allowNegative", "decimalPrecision", "defaultValue", "onChange", "value"]);

  var _useMuiFieldsContext = useMuiFieldsContext(),
      numberDecimalPrecision = _useMuiFieldsContext.numberDecimalPrecision;

  var decimalPrecision = pdp || numberDecimalPrecision; // const thousandSeparator = Globalize.locale().main('numbers/symbols-numberSystem-latn/group');

  var decimalSeparator = Globalize.locale().main('numbers/symbols-numberSystem-latn/decimal');

  var _useState = useState(undefined),
      _useState2 = _slicedToArray(_useState, 2),
      innerDefaultValue = _useState2[0],
      setDefaultValue = _useState2[1];

  useEffect(function () {
    setDefaultValue(stateValue(decimalPrecision, outherDefaultValue));
  }, [decimalPrecision, outherDefaultValue]);

  var _useState3 = useState({
    innerValue: undefined,
    parsedValue: undefined
  }),
      _useState4 = _slicedToArray(_useState3, 2),
      _useState4$ = _useState4[0],
      innerValue = _useState4$.innerValue,
      parsedValue = _useState4$.parsedValue,
      setValue = _useState4[1];

  useEffect(function () {
    if (outherValue !== parsedValue) {
      setValue({
        innerValue: stateValue(decimalPrecision, outherValue),
        parsedValue: outherValue
      });
    }
  }, [outherValue, parsedValue, decimalPrecision]);

  var handleChange = function handleChange(value) {
    var val = (value || '').trim();
    var unformatted;
    var parsed;

    if (!isEmpty(val) && val !== '') {
      var isNegative = val.startsWith('-') && allowNegative;
      var parts = val.split(decimalSeparator).map(function (p) {
        return p.replace(/\D/g, '');
      });
      invariant(parts.length > 0, 'Something is wrong. No parts found.');

      if (parts[0] === '') {
        unformatted = parts.length === 1 ? '' : '0';
      } else {
        parsed = numberParser(parts[0]);
        unformatted = numberFormatter(parsed, 0);
      }

      if (parts.length === 2 && decimalPrecision > 0) {
        var decPart = parts[1].substring(0, decimalPrecision);
        parsed = Number.parseFloat("".concat(parsed, ".").concat(decPart));
        unformatted += "".concat(decimalSeparator).concat(decPart);
      }

      if (isNegative) {
        parsed = -parsed;
        unformatted = "-".concat(unformatted);
      }

      invariant(typeof unformatted !== 'undefined', 'Something is wrong.');
    } else {
      parsed = undefined;
      unformatted = undefined;
    }

    if (Number.isNaN(parsed)) {
      parsed = undefined;
    }

    setValue({
      innerValue: unformatted,
      parsedValue: parsed
    });

    if (onChange) {
      onChange(parsed);
    }
  };

  return /*#__PURE__*/React.createElement(TextField, _extends({}, rest, {
    defaultValue: innerDefaultValue,
    value: innerValue,
    onChange: handleChange
  }));
}

NumberField.propTypes = {
  allowNegative: PropTypes.bool,

  /**
   * This property helps users to fill forms faster, especially on mobile devices.
   * The name can be confusing, as it's more like an autofill. You can learn more about it here:
   * https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
   */
  autoComplete: PropTypes.string,

  /**
   * `true` ak ma mat pole focus po prvom mounte.
   */
  autoFocus: PropTypes.bool,

  /**
   * Jmeno CSS tridy.
   */
  className: PropTypes.string,

  /**
   * Pocet desetinnych mist.
   * Pokud neni definovana pouzite se nastaveni z Provideru.
   */
  decimalPrecision: PropTypes.number,

  /**
   * Defaultni hodnota.
   */
  defaultValue: PropTypes.string,

  /**
   * `true` pokud ma byt field disablovan.
   */
  disabled: PropTypes.bool,

  /**
   * Chybova hlaska.
   */
  error: PropTypes.string,

  /**
   * Pokud je `true` bude se renderovat polozka na plnou dostupnou sirku.
   */
  fullWidth: PropTypes.bool,

  /**
   * Pomocny text, zobrazovany pod `input`em.
   */
  helperText: PropTypes.string,

  /**
   * Hodnota atributu `id` nativniho `input`u.
   */
  id: PropTypes.string,

  /**
   * `Object` s custom vlastnostmi komponenty `InputLabel`.
   */
  InputLabelProps: PropTypes.shape({}),

  /**
   * `Object` s custom vlastnostmi komponenty `Input`.
   */
  InputProps: PropTypes.shape({}),

  /**
   * `Object` s custom vlastnostmi komponenty `input` (nativni).
   */
  inputProps: PropTypes.shape({}),

  /**
   * `Ref` inputu.
   */
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),

  /**
   * Zobrazovany label.
   */
  label: PropTypes.string,

  /**
   * Margin fieldu.
   */
  margin: PropTypes.oneOf(['none', 'dense', 'normal']),

  /**
   * Hodnota atributu `name` nativniho `input`u.
   */
  name: PropTypes.string,

  /**
   * Handler pro obsluhu zmeny.
   *
   * Signatura funkce: `(value: TValue) => void`
   */
  onChange: PropTypes.func.isRequired,

  /**
   * Hodnota zobrazovana, pokud je `value` prazdne / nedefinovane.
   */
  placeholder: PropTypes.string,

  /**
   * `true` pokud ma byt field vyzadovan. Pokud je `true`, automaticky se do labelu prida '*'.
   */
  required: PropTypes.bool,

  /**
   * Objekt stylu.
   */
  style: PropTypes.shape({}),

  /**
   * Hodnota atributu `type` nativniho `input`u.
   */
  type: PropTypes.string,

  /**
   * Hodnota fieldu (`TValue`).
   */
  value: PropTypes.number
};
NumberField.defaultProps = {
  allowNegative: true,
  autoComplete: undefined,
  autoFocus: undefined,
  className: undefined,
  decimalPrecision: undefined,
  defaultValue: undefined,
  disabled: undefined,
  error: undefined,
  fullWidth: undefined,
  helperText: undefined,
  id: undefined,
  InputLabelProps: undefined,
  InputProps: undefined,
  inputProps: undefined,
  inputRef: undefined,
  label: undefined,
  margin: undefined,
  name: undefined,
  placeholder: undefined,
  required: undefined,
  style: undefined,
  type: undefined,
  value: undefined
};
export default NumberField;